import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Truck, ShieldCheck, DollarSign, CreditCard, ArrowRight } from 'lucide-react';

const ValuePropositions = ({ onQuoteClick }) => {
  const navigate = useNavigate();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const boxes = [
    {
      icon: Truck,
      title: 'On-Time Delivery',
      description: 'We coordinate delivery around your schedule, so the lift is ready when your crew needs it.',
      linkText: 'Schedule Delivery',
      action: () => navigate('/contact'),
      testId: 'value-prop-delivery',
    },
    {
      icon: ShieldCheck,
      title: 'Equipment You Can Count On',
      description: 'Our lifts are inspected, serviced, and prepared before delivery so your crew can get to work with confidence.',
      linkText: 'Browse Fleet',
      action: () => navigate('/equipment'),
      testId: 'value-prop-equipment',
    },
    {
      icon: DollarSign,
      title: 'Straightforward Pricing',
      description: 'Out-the-door pricing, clear rental rates, and no hidden cleaning or environmental fees.',
      linkText: 'See Pricing',
      action: () => scrollTo('pricing'),
      testId: 'value-prop-pricing',
    },
    {
      icon: CreditCard,
      title: 'Contractor Credit Available',
      description: 'Approved accounts can rent with credit terms, making repeat orders faster and easier.',
      linkText: 'Request Quote',
      action: onQuoteClick,
      testId: 'value-prop-quote',
    },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Why Cooper Rentals Is a Preferred Rental Partner
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Reliable equipment, clear pricing, and dependable service that helps contractors stay on schedule.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {boxes.map((box, index) => {
            const Icon = box.icon;
            return (
              <button
                key={index}
                type="button"
                onClick={box.action}
                data-testid={box.testId}
                className="group block h-full text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#E28724] focus:ring-offset-2 rounded-lg"
              >
                <Card className="h-full border-2 hover:border-[#E28724] transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl overflow-hidden flex flex-col">
                  <div className="h-2 bg-[#1a2332] transition-all duration-300 group-hover:h-3" />
                  <CardContent className="p-6 flex flex-col flex-grow">
                    <div className="mb-4">
                      <div className="w-14 h-14 rounded-xl bg-[#1a2332] flex items-center justify-center transform transition-transform duration-300 group-hover:scale-110">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#E28724] transition-colors">
                      {box.title}
                    </h3>

                    <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                      {box.description}
                    </p>

                    <div className="flex items-center text-[#E28724] font-semibold group-hover:translate-x-2 transition-transform mt-auto">
                      <span>{box.linkText}</span>
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </div>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ValuePropositions;
